import React, { useEffect, useState } from "react";
import {
  Empty, Input, Select, Spin,
} from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dashboardChartTheme from "../../Dashboard/dashboardChartTheme";
import DashboardBrushTraveller from "../../Dashboard/DashboardBrushTraveller";
import {
  buildOverdueCategoryChartSeries,
  buildOverdueReasonChartSeries,
  buildOverdueSlaChartSeries,
  collapseOverdueDonutSeries,
  OVERDUE_DRILL_DOWN_KIND,
  SLA_AGGREGATE_OPTIONS,
  SLA_OVERALL_DATA_KEY,
} from "../utils/overdueUtils";
import "../../Dashboard/DashboardShared.css";
import "../../Dashboard/TestMetrics.css";

/** Categorical palette for donut segments — anchored to dashboard brand purple. */
const DONUT_SEGMENT_COLORS = [
  dashboardChartTheme.sampleSeries.product,
  dashboardChartTheme.testMetricsSeries.pending,
  dashboardChartTheme.testMetricsSeries.onTime,
  dashboardChartTheme.testMetricsSeries.delayed,
  dashboardChartTheme.sampleSeries.environment,
  dashboardChartTheme.testMetricsSeries.total,
  "#8B6F47",
  "#4A7C8F",
  "#9B6B9E",
  "#5B8A9A",
];

/** Stroke + soft fill pairs for SLA series pills / lines (ESV Volume Trend pattern). */
const SLA_SERIES_PALETTE = [
  { color: dashboardChartTheme.sampleSeries.product, backgroundColor: "rgba(83, 85, 147, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.pending, backgroundColor: "rgba(112, 121, 200, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.onTime, backgroundColor: "rgba(90, 170, 110, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.delayed, backgroundColor: "rgba(214, 93, 82, 0.12)" },
  { color: dashboardChartTheme.sampleSeries.environment, backgroundColor: "rgba(92, 154, 122, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.total, backgroundColor: "rgba(201, 168, 92, 0.12)" },
  { color: "#8B6F47", backgroundColor: "rgba(139, 111, 71, 0.12)" },
  { color: "#4A7C8F", backgroundColor: "rgba(74, 124, 143, 0.12)" },
  { color: "#9B6B9E", backgroundColor: "rgba(155, 107, 158, 0.12)" },
  { color: "#5B8A9A", backgroundColor: "rgba(91, 138, 154, 0.12)" },
];

/** Donut fills most of the card height; callouts use the remaining outer ring. */
const DONUT_INNER_RADIUS = 72;
const DONUT_OUTER_RADIUS = 110;
const DONUT_CHART_HEIGHT = 300;
const DONUT_LABEL_MAX_CHARS = 12;
/** Hide callout text for tiny named slices (still hoverable). */
const DONUT_MIN_LABEL_PERCENT = 0.04;
const DONUT_RADIAN = Math.PI / 180;

const SLA_DOT_RADIUS = 4;
const SLA_ACTIVE_DOT_RADIUS = 5;
const SLA_HOVER_STROKE = dashboardChartTheme.sampleSeries.product;
const SLA_CHART_HEIGHT = 360;
const SLA_BRUSH_GUTTER = 20;

/** Compact brush edge labels (full value remains on the chart axis / tooltip). */
const formatBrushTickLabel = (value) => {
  const label = String(value ?? "");
  if (label.length <= 12) {
    return label;
  }
  return `${label.slice(0, 11)}…`;
};

/**
 * Bar layout for the narrow Data Ops charts pane.
 * Keep margins modest so Recharts does not force the page wider than the pane.
 */
const OVERDUE_BAR_LAYOUT = {
  responsiveHeight: 360,
  marginTop: 12,
  marginRight: 12,
  marginLeft: 4,
  marginBottom: 8,
  xAxisHeight: 72,
  xAxisTickMargin: 8,
  brushLabelGutter: 20,
  maxBarSize: 48,
};

/** Max visible chars on angled reason-axis ticks; full name is in SVG title (hover). */
const REASON_AXIS_TICK_MAX_CHARS = 8;

/**
 * Angled X-axis tick with ellipsis + native tooltip for the full reason label.
 */
function ReasonAxisTick({ x, y, payload }) {
  const fullLabel = String(payload?.value ?? "");
  const displayLabel = fullLabel.length > REASON_AXIS_TICK_MAX_CHARS
    ? `${fullLabel.slice(0, REASON_AXIS_TICK_MAX_CHARS - 1)}…`
    : fullLabel;

  return (
    <g transform={`translate(${x},${y})`}>
      <title>{fullLabel}</title>
      <text
        dy={10}
        textAnchor="end"
        transform="rotate(-45)"
        fontSize={11}
        fill="#595959"
      >
        {displayLabel}
      </text>
    </g>
  );
}

const getChartSegmentColor = (index) => (
  DONUT_SEGMENT_COLORS[index % DONUT_SEGMENT_COLORS.length]
);

/**
 * Palette entry for an SLA line descriptor.
 * Overall always uses brand purple (index 0); categories cycle from index 1.
 * @param {{ dataKey: string, categoryIndex: number }} descriptor
 * @returns {{ color: string, backgroundColor: string }}
 */
const getSlaSeriesPalette = (descriptor) => {
  if (descriptor.dataKey === SLA_OVERALL_DATA_KEY) {
    return SLA_SERIES_PALETTE[0];
  }
  const paletteIndex = (Math.max(descriptor.categoryIndex, 0) % (SLA_SERIES_PALETTE.length - 1)) + 1;
  return SLA_SERIES_PALETTE[paletteIndex];
};

/**
 * Format a chart SLA percentage for tooltips / axis ticks.
 * @param {*} value
 * @returns {string}
 */
const formatSlaPercentLabel = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "0%";
  }
  return `${parsed.toFixed(1)}%`;
};

/** Keep wheel / drag on the tooltip from bubbling into the Recharts canvas. */
const stopTooltipScrollPropagation = (event) => {
  event.stopPropagation();
};

const CHART_TOOLTIP_WRAPPER_STYLE = {
  pointerEvents: "auto",
  zIndex: 20,
};

/**
 * Shared Overdue chart tooltip shell (category, reason, SLA).
 * Header + optional View details + pill rows — same layout for every chart.
 */
function OverdueAnalyticsTooltip({
  active,
  title,
  rows,
  onViewDetails,
}) {
  if (!active || !title) {
    return null;
  }

  const tooltipRows = Array.isArray(rows) ? rows : [];

  const handleViewDetails = (event) => {
    event.stopPropagation();
    if (onViewDetails) {
      onViewDetails();
    }
  };

  return (
    <div
      className="DataOpsAnalytics__ChartTooltip"
      onMouseDown={stopTooltipScrollPropagation}
      onTouchStart={stopTooltipScrollPropagation}
    >
      <div className="DataOpsAnalytics__ChartTooltipHeader">
        <span className="DataOpsAnalytics__ChartTooltipTitle">{title}</span>
        {onViewDetails ? (
          <button
            type="button"
            className="DataOpsAnalytics__ChartTooltipViewDetails"
            onClick={handleViewDetails}
          >
            View details
          </button>
        ) : null}
      </div>
      <div
        className="DataOpsAnalytics__ChartTooltipList"
        onWheel={stopTooltipScrollPropagation}
        onScroll={stopTooltipScrollPropagation}
      >
        {tooltipRows.map((row) => (
          <div key={row.key} className="DataOpsAnalytics__ChartTooltipRow">
            <div className="DataOpsAnalytics__ChartTooltipRowLabel">
              <span
                className="DataOpsAnalytics__ChartTooltipDot"
                style={{ backgroundColor: row.color }}
              />
              <span>{row.label}</span>
            </div>
            <span className="DataOpsAnalytics__ChartTooltipValue">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryChartTooltip({ active, payload, onViewDetails }) {
  const point = payload?.[0]?.payload;
  if (!active || !point) {
    return null;
  }

  const countEntry = payload.find((entry) => entry.dataKey === "count") || payload[0];
  const countColor = countEntry?.color || DONUT_SEGMENT_COLORS[0];
  const breakdownItems = point.otherBreakdown?.length
    ? point.otherBreakdown
    : (point.reasonBreakdown || []);

  const tooltipRows = [
    {
      key: `${point.key}-count`,
      label: "Count",
      value: String(point.count ?? 0),
      color: countColor,
    },
    ...breakdownItems.map((item, index) => ({
      key: `${point.key}-${item.key || item.name}-${index}`,
      label: item.name,
      value: String(item.count ?? 0),
      color: getChartSegmentColor(index),
    })),
  ];

  return (
    <OverdueAnalyticsTooltip
      active={active}
      title={point.name}
      rows={tooltipRows}
      onViewDetails={
        onViewDetails
          ? () => onViewDetails(point)
          : undefined
      }
    />
  );
}

/**
 * Truncate long category names for on-pie callouts.
 * @param {string} name
 * @returns {string}
 */
const truncateDonutLabel = (name) => {
  const safeName = String(name ?? "");
  if (safeName.length <= DONUT_LABEL_MAX_CHARS) {
    return safeName;
  }
  return `${safeName.slice(0, DONUT_LABEL_MAX_CHARS - 1)}…`;
};

/**
 * External callout name on the donut (not a legend).
 * Skips tiny slices so named wedges stay readable.
 */
function CategoryDonutLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  name,
  percent,
  showLabel,
}) {
  if (!showLabel || percent < DONUT_MIN_LABEL_PERCENT) {
    return null;
  }

  const radius = outerRadius + 16;
  const x = cx + radius * Math.cos(-midAngle * DONUT_RADIAN);
  const y = cy + radius * Math.sin(-midAngle * DONUT_RADIAN);

  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="DataOpsAnalytics__DonutLabel"
    >
      {truncateDonutLabel(name)}
    </text>
  );
}

/** Match label visibility so tiny slices do not get orphan leader lines. */
function CategoryDonutLabelLine({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  showLabel,
}) {
  if (!showLabel || percent < DONUT_MIN_LABEL_PERCENT) {
    return null;
  }

  const startRadius = outerRadius + 2;
  const endRadius = outerRadius + 14;
  const startX = cx + startRadius * Math.cos(-midAngle * DONUT_RADIAN);
  const startY = cy + startRadius * Math.sin(-midAngle * DONUT_RADIAN);
  const endX = cx + endRadius * Math.cos(-midAngle * DONUT_RADIAN);
  const endY = cy + endRadius * Math.sin(-midAngle * DONUT_RADIAN);

  return (
    <path
      d={`M${startX},${startY}L${endX},${endY}`}
      stroke="#bfbfbf"
      fill="none"
      strokeWidth={1}
    />
  );
}

function ReasonChartTooltip({ active, payload, onViewDetails }) {
  const point = payload?.[0]?.payload;
  if (!active || !point) {
    return null;
  }

  const countEntry = payload.find((entry) => entry.dataKey === "count") || payload[0];
  const countColor = countEntry?.color || DONUT_SEGMENT_COLORS[0];
  const breakdownItems = point.categoryBreakdown || [];

  const tooltipRows = [
    {
      key: `${point.key}-count`,
      label: "Count",
      value: String(point.count ?? 0),
      color: countColor,
    },
    ...breakdownItems.map((item, index) => ({
      key: `${point.key}-${item.key || item.name}-${index}`,
      label: item.name,
      value: String(item.count ?? 0),
      color: getChartSegmentColor(index),
    })),
  ];

  return (
    <OverdueAnalyticsTooltip
      active={active}
      title={point.name}
      rows={tooltipRows}
      onViewDetails={
        onViewDetails
          ? () => onViewDetails(point)
          : undefined
      }
    />
  );
}

/** Dashed vertical hover cursor — same idea as ESV TrendChartCursor. */
function SlaTrendCursor({ points, height }) {
  if (!points?.length) {
    return null;
  }
  const { x } = points[0];
  return (
    <line
      x1={x}
      y1={0}
      x2={x}
      y2={height}
      stroke={SLA_HOVER_STROKE}
      strokeWidth={1}
      strokeDasharray="4 4"
    />
  );
}

/**
 * SLA tooltip — same shell as category/reason; values are percentages.
 */
function SlaTrendTooltip({
  active,
  payload,
  seriesColorByKey,
  onViewDetails,
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  const tooltipRows = [...payload]
    .filter((entry) => entry.value != null)
    .sort((left, right) => Number(right.value) - Number(left.value))
    .map((entry) => ({
      key: entry.dataKey,
      label: entry.name,
      value: formatSlaPercentLabel(entry.value),
      color: seriesColorByKey?.[entry.dataKey]?.color || entry.color,
    }));

  return (
    <OverdueAnalyticsTooltip
      active={active}
      title={`SLA for ${point.periodLabel}`}
      rows={tooltipRows}
      onViewDetails={
        onViewDetails
          ? () => onViewDetails(point, tooltipRows[0]?.key || payload[0]?.dataKey)
          : undefined
      }
    />
  );
}

function ChartEmptyState({ title, description }) {
  return (
    <div className="DataOpsAnalytics__ChartEmpty">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={description || `No data for ${title}`}
      />
    </div>
  );
}

/**
 * Vertical bar + Brush — Dashboard brush/traveller pattern, margins tuned for this pane.
 * Handles long reason lists (brush window) instead of cramming every tick.
 * Bar click / tooltip View details opens reason drill-down.
 * Brush must be a direct BarChart child (Recharts does not detect wrapped Brush).
 */
function OverdueBrushedBarChart({
  series,
  emptyTitle,
  onBarClick,
}) {
  if (!series.length) {
    return <ChartEmptyState title={emptyTitle} />;
  }

  return (
    <ResponsiveContainer width="100%" height={OVERDUE_BAR_LAYOUT.responsiveHeight}>
      <BarChart
        data={series}
        margin={{
          top: OVERDUE_BAR_LAYOUT.marginTop,
          right: OVERDUE_BAR_LAYOUT.marginRight + OVERDUE_BAR_LAYOUT.brushLabelGutter,
          left: OVERDUE_BAR_LAYOUT.marginLeft + OVERDUE_BAR_LAYOUT.brushLabelGutter,
          bottom: OVERDUE_BAR_LAYOUT.marginBottom,
        }}
      >
        <CartesianGrid
          stroke={dashboardChartTheme.gridStroke}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="name"
          height={OVERDUE_BAR_LAYOUT.xAxisHeight}
          tickMargin={OVERDUE_BAR_LAYOUT.xAxisTickMargin}
          minTickGap={20}
          interval={0}
          tick={<ReasonAxisTick />}
        />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={40} />
        <Tooltip
          wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
          content={(tooltipProps) => (
            <ReasonChartTooltip
              active={tooltipProps.active}
              payload={tooltipProps.payload}
              onViewDetails={onBarClick}
            />
          )}
        />
        <Bar
          dataKey="count"
          name="Count"
          maxBarSize={OVERDUE_BAR_LAYOUT.maxBarSize}
          cursor={onBarClick ? "pointer" : "default"}
          onClick={(clickData) => {
            if (!onBarClick || !clickData) {
              return;
            }
            // Prefer the original series point; Recharts may wrap it under payload.
            const point = clickData.payload && !Array.isArray(clickData.payload)
              ? clickData.payload
              : clickData;
            onBarClick(point);
          }}
        >
          {series.map((point, index) => (
            <Cell
              key={point.key}
              fill={getChartSegmentColor(index)}
            />
          ))}
        </Bar>
        <Brush
          dataKey="name"
          stroke={dashboardChartTheme.brush.stroke}
          fill={dashboardChartTheme.brush.fill}
          height={dashboardChartTheme.brush.height}
          travellerWidth={dashboardChartTheme.brush.travellerWidth}
          traveller={DashboardBrushTraveller}
          tickFormatter={formatBrushTickLabel}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CategoryDonutChart({ series, onSliceClick }) {
  const donutSeries = collapseOverdueDonutSeries(series);

  if (!donutSeries.length) {
    return <ChartEmptyState title="category distribution" />;
  }

  return (
    <div className="DataOpsAnalytics__DonutChart">
      <ResponsiveContainer width="100%" height={DONUT_CHART_HEIGHT}>
        <PieChart
          margin={{
            top: 8,
            right: 8,
            bottom: 8,
            left: 8,
          }}
        >
          <Pie
            data={donutSeries}
            dataKey="count"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={DONUT_INNER_RADIUS}
            outerRadius={DONUT_OUTER_RADIUS}
            paddingAngle={1}
            label={CategoryDonutLabel}
            labelLine={CategoryDonutLabelLine}
            cursor={onSliceClick ? "pointer" : "default"}
            onClick={(point) => {
              if (!onSliceClick || !point) {
                return;
              }
              // Recharts may pass sector props with original fields on payload or flattened.
              onSliceClick(point.payload || point);
            }}
          >
            {donutSeries.map((point, index) => (
              <Cell
                key={point.key}
                fill={getChartSegmentColor(index)}
              />
            ))}
          </Pie>
          <Tooltip
            wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
            content={(tooltipProps) => (
              <CategoryChartTooltip
                active={tooltipProps.active}
                payload={tooltipProps.payload}
                onViewDetails={onSliceClick}
              />
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function SlaSeriesPill({
  label,
  color,
  backgroundColor,
  isSelected,
  onToggle,
}) {
  return (
    <button
      type="button"
      className={
        isSelected
          ? "DataOpsAnalytics__SlaPill DataOpsAnalytics__SlaPill--active"
          : "DataOpsAnalytics__SlaPill DataOpsAnalytics__SlaPill--inactive"
      }
      style={isSelected ? { borderColor: color, color, backgroundColor } : undefined}
      onClick={onToggle}
      aria-pressed={isSelected}
    >
      <span className="DataOpsAnalytics__SlaPillLabel">{label}</span>
      {isSelected
        ? <CloseOutlined className="DataOpsAnalytics__SlaPillIcon" aria-hidden />
        : <PlusOutlined className="DataOpsAnalytics__SlaPillIcon" aria-hidden />}
    </button>
  );
}

function SlaTrendDot({
  cx,
  cy,
  stroke,
  value,
  payload,
  dataKey,
  onDotClick,
  radius = SLA_DOT_RADIUS,
}) {
  if (value == null || cx == null || cy == null) {
    return null;
  }

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill="#ffffff"
      stroke={stroke}
      strokeWidth={2}
      className={
        onDotClick
          ? "DataOpsAnalytics__SlaDot DataOpsAnalytics__SlaDot--clickable"
          : "DataOpsAnalytics__SlaDot"
      }
      onClick={(event) => {
        if (!onDotClick) {
          return;
        }
        event.stopPropagation();
        onDotClick(payload, dataKey);
      }}
    />
  );
}

/**
 * Multi-line SLA trend — UI aligned with ESV Environment Analytics Volume Trend.
 * Brush is in-chart (same pattern as the reason bar) so the window can be resized freely.
 * Independent loading / error / empty from the phase-driven charts above.
 * Dot click / View details → SLA drill-down (filtered overdue subset for that category).
 */
function SlaTrendChart({
  seriesList,
  loading,
  errorMessage,
  onDrillDown,
}) {
  const [selectedSeriesKeys, setSelectedSeriesKeys] = useState([]);
  const [seriesSearchText, setSeriesSearchText] = useState("");

  // Keep prior series while refetching (aggregate switch) so card height stays stable.
  const { chartRows, lineDescriptors } = buildOverdueSlaChartSeries(
    errorMessage && !loading ? [] : seriesList,
  );
  const seriesSignature = lineDescriptors.map((descriptor) => descriptor.dataKey).join("|");

  useEffect(() => {
    // Filter-driven SLA lines — select every requested series (not a fixed top-N).
    setSelectedSeriesKeys(
      lineDescriptors.map((descriptor) => descriptor.dataKey),
    );
    setSeriesSearchText("");
  }, [seriesSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !chartRows.length) {
    return (
      <div className="DataOpsAnalytics__ChartEmpty DataOpsAnalytics__SlaTrendLoading">
        <Spin />
      </div>
    );
  }

  if (errorMessage && !chartRows.length) {
    return (
      <ChartEmptyState
        title="SLA trend"
        description={errorMessage}
      />
    );
  }

  if (!chartRows.length) {
    return (
      <div className="DataOpsAnalytics__ChartEmpty DataOpsAnalytics__SlaTrendLoading">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No data for SLA trend"
        />
      </div>
    );
  }

  const seriesColorByKey = {};
  lineDescriptors.forEach((descriptor) => {
    seriesColorByKey[descriptor.dataKey] = getSlaSeriesPalette(descriptor);
  });

  const normalizedSeriesSearch = seriesSearchText.trim().toLowerCase();
  const filteredLineDescriptors = normalizedSeriesSearch
    ? lineDescriptors.filter((descriptor) => (
      descriptor.legendName.toLowerCase().includes(normalizedSeriesSearch)
    ))
    : lineDescriptors;

  const visibleDescriptors = lineDescriptors.filter((descriptor) => (
    selectedSeriesKeys.includes(descriptor.dataKey)
  ));

  const toggleSeries = (dataKey) => {
    setSelectedSeriesKeys((previousKeys) => (
      previousKeys.includes(dataKey)
        ? previousKeys.filter((key) => key !== dataKey)
        : [...previousKeys, dataKey]
    ));
  };

  const openSlaDrillDown = (periodPoint, categoryKey = SLA_OVERALL_DATA_KEY) => {
    if (!onDrillDown || !periodPoint) {
      return;
    }

    const seriesDescriptor = lineDescriptors.find(
      (descriptor) => descriptor.dataKey === categoryKey,
    );
    const seriesLabel = categoryKey === SLA_OVERALL_DATA_KEY
      ? "All categories"
      : (seriesDescriptor?.legendName || "Category");
    const slaValue = periodPoint[categoryKey];
    const periodRangeLabel = periodPoint.periodStart && periodPoint.periodEnd
      ? `${periodPoint.periodStart} – ${periodPoint.periodEnd}`
      : periodPoint.periodLabel;

    onDrillDown({
      kind: OVERDUE_DRILL_DOWN_KIND.SLA,
      categoryKey,
      periodLabel: periodPoint.periodLabel,
      periodStart: periodPoint.periodStart,
      periodEnd: periodPoint.periodEnd,
      title: `SLA — ${periodPoint.periodLabel}`,
      subtitle: `${seriesLabel}: ${formatSlaPercentLabel(slaValue)} · Period ${periodRangeLabel}`,
    });
  };

  return (
    <Spin spinning={loading}>
      <div className="DataOpsAnalytics__SlaTrend">
        <div className="DataOpsAnalytics__SlaSeriesControls">
          <Input.Search
            allowClear
            className="DataOpsAnalytics__Search DataOpsAnalytics__SlaSeriesSearch"
            placeholder="Search categories"
            value={seriesSearchText}
            onChange={(event) => setSeriesSearchText(event.target.value)}
          />
          <div className="DataOpsAnalytics__SlaPills">
            {filteredLineDescriptors.length === 0 ? (
              <span className="DataOpsAnalytics__SlaSeriesEmpty">
                No categories match your search.
              </span>
            ) : (
              filteredLineDescriptors.map((descriptor) => {
                const palette = seriesColorByKey[descriptor.dataKey];
                return (
                  <SlaSeriesPill
                    key={descriptor.dataKey}
                    label={descriptor.legendName}
                    color={palette.color}
                    backgroundColor={palette.backgroundColor}
                    isSelected={selectedSeriesKeys.includes(descriptor.dataKey)}
                    onToggle={() => toggleSeries(descriptor.dataKey)}
                  />
                );
              })
            )}
          </div>
        </div>

        {visibleDescriptors.length === 0 ? (
          <div className="DataOpsAnalytics__SlaPlaceholder" role="alert">
            Please select at least one series to display the graph.
          </div>
        ) : (
          <div className="DataOpsAnalytics__SlaChartWrapper">
            <div className="DataOpsAnalytics__SlaChartArea">
              <ResponsiveContainer width="100%" height={SLA_CHART_HEIGHT}>
                <LineChart
                  data={chartRows}
                  margin={{
                    top: 10,
                    right: 16 + SLA_BRUSH_GUTTER,
                    left: SLA_BRUSH_GUTTER,
                    bottom: 10,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke={dashboardChartTheme.gridStroke}
                    vertical
                    horizontal
                  />
                  <XAxis
                    dataKey="periodLabel"
                    tickLine={false}
                    axisLine={false}
                    tick={{
                      fill: "#6b778c",
                      fontSize: 11,
                      fontFamily: "Roboto",
                    }}
                    interval="preserveStartEnd"
                    padding={{ left: 10, right: 10 }}
                    height={36}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={44}
                    tick={{
                      fill: "#6b778c",
                      fontSize: 11,
                      fontFamily: "Roboto",
                    }}
                    tickFormatter={formatSlaPercentLabel}
                    domain={[0, "auto"]}
                  />
                  <Tooltip
                    wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
                    content={(tooltipProps) => (
                      <SlaTrendTooltip
                        active={tooltipProps.active}
                        payload={tooltipProps.payload}
                        seriesColorByKey={seriesColorByKey}
                        onViewDetails={
                          onDrillDown
                            ? (periodPoint, seriesKey) => openSlaDrillDown(
                              periodPoint,
                              seriesKey
                                || visibleDescriptors[0]?.dataKey
                                || SLA_OVERALL_DATA_KEY,
                            )
                            : undefined
                        }
                      />
                    )}
                    cursor={<SlaTrendCursor />}
                  />
                  {visibleDescriptors.map((descriptor) => {
                    const stroke = seriesColorByKey[descriptor.dataKey].color;
                    return (
                      <Line
                        key={descriptor.dataKey}
                        type={dashboardChartTheme.line.curveType}
                        dataKey={descriptor.dataKey}
                        name={descriptor.legendName}
                        stroke={stroke}
                        strokeWidth={dashboardChartTheme.line.strokeWidth}
                        strokeLinecap={dashboardChartTheme.line.strokeLinecap}
                        strokeLinejoin={dashboardChartTheme.line.strokeLinejoin}
                        connectNulls
                        isAnimationActive={false}
                        dot={(dotProps) => (
                          <SlaTrendDot
                            cx={dotProps.cx}
                            cy={dotProps.cy}
                            value={dotProps.value}
                            stroke={stroke}
                            payload={dotProps.payload}
                            dataKey={descriptor.dataKey}
                            onDotClick={onDrillDown ? openSlaDrillDown : undefined}
                          />
                        )}
                        activeDot={(dotProps) => (
                          <SlaTrendDot
                            cx={dotProps.cx}
                            cy={dotProps.cy}
                            value={dotProps.value}
                            stroke={SLA_HOVER_STROKE}
                            radius={SLA_ACTIVE_DOT_RADIUS}
                            payload={dotProps.payload}
                            dataKey={descriptor.dataKey}
                            onDotClick={onDrillDown ? openSlaDrillDown : undefined}
                          />
                        )}
                      />
                    );
                  })}
                  <Brush
                    dataKey="periodLabel"
                    stroke={dashboardChartTheme.brush.stroke}
                    fill={dashboardChartTheme.brush.fill}
                    height={dashboardChartTheme.brush.height}
                    travellerWidth={dashboardChartTheme.brush.travellerWidth}
                    traveller={DashboardBrushTraveller}
                    tickFormatter={formatBrushTickLabel}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
}

function ChartCard({
  title, headerExtra, fullWidth = false, children,
}) {
  return (
    <div
      className={
        fullWidth
          ? "TestMetrics__ChartCard DataOpsAnalytics__ChartCard DataOpsAnalytics__ChartCard--fullWidth"
          : "TestMetrics__ChartCard DataOpsAnalytics__ChartCard"
      }
    >
      <div className="TestMetrics__ChartHeader DataOpsAnalytics__ChartHeader">
        <h3>{title}</h3>
        {headerExtra}
      </div>
      {children}
    </div>
  );
}

function PhaseChartsLoading() {
  return (
    <div className="DataOpsAnalytics__ChartEmpty">
      <Spin tip="Loading charts..." />
    </div>
  );
}

/**
 * Overdue charts — SLA trend full width on top; category + reason on one row below.
 * Category donut + reason bar from phase rows; SLA line from overdue_sla periods.
 * Phase and SLA loading/error are independent so one API failure does not blank the other.
 * Chart clicks open drill-down via onDrillDown (parent fetches export API + opens modal).
 * Collapsed "Other" slices are not drillable — only slices with resolvable API ids.
 */
export default function OverdueCharts({
  rows,
  loading,
  reasonLabelById = null,
  slaSeries,
  slaLoading,
  slaErrorMessage,
  slaAggregate,
  onSlaAggregateChange,
  onDrillDown,
}) {
  const categorySeries = buildOverdueCategoryChartSeries(rows, reasonLabelById);
  const reasonSeries = buildOverdueReasonChartSeries(rows, reasonLabelById);
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const phaseChartsLoading = loading && !hasRows;

  const handleCategorySliceClick = (point) => {
    if (!onDrillDown || !point?.key) {
      return;
    }

    onDrillDown({
      kind: OVERDUE_DRILL_DOWN_KIND.CATEGORY,
      seriesKey: point.key,
      testCategoryId: point.testCategoryId ?? null,
      title: point.name || "Category",
      subtitle: `Count: ${point.count ?? 0}`,
    });
  };

  const handleReasonBarClick = (point) => {
    if (!onDrillDown || !point) {
      return;
    }

    const reasonId = point.reasonId ?? null;
    const seriesKey = point.key
      || (reasonId != null && reasonId !== "" ? `id:${reasonId}` : null);

    if (!seriesKey) {
      return;
    }

    onDrillDown({
      kind: OVERDUE_DRILL_DOWN_KIND.REASON,
      seriesKey,
      reasonId,
      title: point.name || "Reason",
      subtitle: `Count: ${point.count ?? 0}`,
    });
  };

  return (
    <div className="DataOpsAnalytics__ChartsStack">
      <ChartCard
        title="SLA trend"
        fullWidth
        headerExtra={(
          <div className="TestMetrics__FrequencyDropdown">
            <Select
              value={slaAggregate}
              onChange={onSlaAggregateChange}
              options={SLA_AGGREGATE_OPTIONS}
            />
          </div>
        )}
      >
        <SlaTrendChart
          seriesList={slaSeries}
          loading={slaLoading}
          errorMessage={slaErrorMessage}
          onDrillDown={onDrillDown}
        />
      </ChartCard>

      <ChartCard title="Overdue by test category">
        {phaseChartsLoading
          ? <PhaseChartsLoading />
          : (
            <CategoryDonutChart
              series={categorySeries}
              onSliceClick={onDrillDown ? handleCategorySliceClick : undefined}
            />
          )}
      </ChartCard>

      <ChartCard title="Overdue by reason">
        {phaseChartsLoading
          ? <PhaseChartsLoading />
          : (
            <OverdueBrushedBarChart
              series={reasonSeries}
              emptyTitle="overdue reasons"
              onBarClick={onDrillDown ? handleReasonBarClick : undefined}
            />
          )}
      </ChartCard>
    </div>
  );
}
